import {
  type CSSProperties,
  type ElementType,
  type FocusEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { renderInlineTextWithImages } from '../../../utils/inlineText';
import { useEditMode } from './useEditMode';
import { useSectionId, useSectionConfig } from './SectionContext';
import { InlineFormatToolbar, ensureFontFromFamily, type StyleOverride } from './InlineFormatToolbar';

interface EditableProps {
  /** Dot-notation field path the admin save layer understands. */
  field: string;
  /** Current value rendered when not editing. */
  value: string;
  /** Called on blur with the trimmed final string when it differs from `value`. */
  onChange?: (next: string) => void;
  /** Wrapper element. Defaults to <span>. Use 'h1' / 'p' / etc. */
  as?: ElementType;
  className?: string;
  /** Inline styles forwarded to the wrapper in both view + edit modes. */
  style?: CSSProperties;
  /** Optional placeholder shown in edit mode when value is empty. */
  placeholder?: string;
  /** Children render path (rare — used when wrapping non-text content). */
  children?: ReactNode;
  /** Style-only: the element opens the format toolbar (colour/size/font/weight/
   *  spacing/Fill) but its TEXT is NOT editable. Use for computed/numeric values
   *  like prices where the value must stay intact but should be restylable. */
  styleOnly?: boolean;
}

// Turn a field path ("config.faqs.0.question") into a flat, dot-free key so it
// can be a single object key under config.inlineStyles.
const styleKeyFor = (field: string) => field.replace(/[^a-zA-Z0-9]+/g, '_');

const kebab = (k: string) => k.replace(/([A-Z])/g, '-$1').toLowerCase();
const camel = (k: string) => k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

// A "Fill" (element background) applies to the nearest chip/button/card box that
// wraps the editable text — NOT the text node itself (whose box is usually just
// the glyphs). Walk up from the editable element and pick the first ancestor
// (or self) that already reads like a contained box: it has a solid background
// or a rounded corner, and it's clearly narrower than the whole section (so we
// never accidentally fill the entire section). Falls back to the element itself
// (a text highlight) when there's no such box. Deterministic on the stable
// template DOM, so it re-applies identically for shoppers on reload.
function findFillTarget(el: HTMLElement): HTMLElement {
  const section = el.closest('[data-section-id]') as HTMLElement | null;
  const sectionW = section?.getBoundingClientRect().width || Number.POSITIVE_INFINITY;
  let node: HTMLElement | null = el;
  for (let i = 0; node && i < 6; i++) {
    if (section && node === section) break;
    const cs = getComputedStyle(node);
    const hasBg = cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && cs.backgroundColor !== 'transparent';
    const radius = parseFloat(cs.borderRadius) || 0;
    const w = node.getBoundingClientRect().width;
    if ((hasBg || radius >= 6) && w <= sectionW * 0.75) return node;
    node = node.parentElement;
  }
  return el;
}

/**
 * Click-to-edit primitive for storefront copy.
 *
 * - Normal mode: renders `value` inside `as` element, applying any persisted
 *   per-field style override (config.inlineStyles[key]). Zero edit chrome.
 * - Edit mode (?adminEdit=1): element becomes contentEditable with a dashed
 *   outline; focusing it opens a floating format toolbar (size/family/colour/
 *   weight/spacing). Text edits fire on blur; style edits fire immediately.
 *
 * All edits postMessage to the admin parent, which debounces + persists via the
 * inline-edit endpoint. This component is presentational — no API calls here.
 */
export function Editable({
  field,
  value,
  onChange,
  as: Tag = 'span',
  className,
  style,
  placeholder,
  children,
  styleOnly = false,
}: EditableProps) {
  const { editing } = useEditMode();
  const sectionId = useSectionId();
  const sectionConfig = useSectionConfig();
  const ref = useRef<HTMLElement | null>(null);
  const [toolbarOpen, setToolbarOpen] = useState(false);

  const styleKey = useMemo(() => styleKeyFor(field), [field]);
  const persisted: StyleOverride | undefined = sectionConfig?.inlineStyles?.[styleKey];
  const persistedJSON = persisted ? JSON.stringify(persisted) : '';
  const [override, setOverride] = useState<StyleOverride>(persisted || {});

  // Re-sync the override when the persisted config changes (e.g. after a reload
  // or a config push from the admin parent).
  useEffect(() => {
    setOverride(persisted || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedJSON]);

  // Ensure a persisted Google font renders for shoppers, not just the merchant.
  useEffect(() => {
    ensureFontFromFamily(override.fontFamily as string | undefined);
  }, [override.fontFamily]);

  // Apply the per-field overrides with `!important` directly on the DOM node so
  // they beat template CSS that marks hero/section fonts `!important` (a plain
  // React inline style loses to those). Runs after every render to re-assert
  // itself if React re-applies the base style. Cleared props fall back to the
  // component's own base style (or are removed if it had none).
  const appliedRef = useRef<Set<string>>(new Set());
  const fillRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const base = (style || {}) as Record<string, any>;
    const next = new Set<string>();
    Object.entries(override).forEach(([k, v]) => {
      if (v == null || v === '') return;
      if (k === 'backgroundColor') return; // element fill — applied to the box below
      const p = kebab(k);
      el.style.setProperty(p, String(v), 'important');
      next.add(p);
    });
    appliedRef.current.forEach((p) => {
      if (!next.has(p)) {
        const c = camel(p);
        if (base[c] != null) el.style.setProperty(p, String(base[c]));
        else el.style.removeProperty(p);
      }
    });
    appliedRef.current = next;

    // Element "Fill": paint the nearest chip/button/card box. Re-target every
    // render (DOM is deterministic) and revert the previous box if the fill was
    // cleared or moved.
    const fillColor = override.backgroundColor;
    const target = fillColor ? findFillTarget(el) : null;
    if (fillRef.current && fillRef.current !== target) {
      fillRef.current.style.removeProperty('background-color');
      fillRef.current = null;
    }
    if (target && fillColor) {
      target.style.setProperty('background-color', String(fillColor), 'important');
      fillRef.current = target;
    }
  });

  // Sync external text value into the contentEditable DOM when not focused.
  // (Not for styleOnly — React owns that content; we must not clobber it.)
  useEffect(() => {
    if (!editing || styleOnly) return;
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if ((el.textContent ?? '') !== value) {
      el.textContent = value;
    }
  }, [editing, value]);

  // Close the toolbar on an outside click.
  useEffect(() => {
    if (!toolbarOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (ref.current?.contains(t)) return;
      if (t.closest('[data-qe-toolbar]')) return;
      setToolbarOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [toolbarOpen]);

  const postToParent = (message: Record<string, unknown>) => {
    if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(message, '*');
      } catch {
        /* noop — parent may have been unloaded */
      }
    }
  };

  const persistStyleProp = (prop: string, val: string | number | undefined) => {
    if (sectionId == null) return;
    postToParent({
      type: 'section-inline-edit',
      sectionId,
      field: `config.inlineStyles.${styleKey}.${prop}`,
      value: val,
    });
  };

  const applyStyle = (patch: StyleOverride) => {
    setOverride((prev) => ({ ...prev, ...patch }));
    Object.entries(patch).forEach(([prop, val]) => persistStyleProp(prop, val));
  };

  const resetStyle = () => {
    setOverride({});
    if (sectionId == null) return;
    postToParent({
      type: 'section-inline-edit',
      sectionId,
      field: `config.inlineStyles.${styleKey}`,
      value: {},
    });
  };

  // Section background colour is handled by the dedicated per-section
  // "Background" pill (SectionBgControl), not this text toolbar.

  if (!editing) {
    return (
      <Tag ref={ref as any} className={className} style={style} data-field={field}>
        {children ?? renderInlineTextWithImages(value, styleKey, 1)}
      </Tag>
    );
  }

  const handleBlur = (e: FocusEvent<HTMLElement>) => {
    const next = (e.currentTarget.textContent ?? '').trim();
    if (next === value) return;
    onChange?.(next);
    // The storefront renders inside an iframe in the admin editor. Forward the
    // edit to the parent window (which has auth) to debounce + persist. With a
    // sectionId the path is section-relative (merged into customSections);
    // otherwise it's a top-level inline-edit path.
    postToParent(
      sectionId != null
        ? { type: 'section-inline-edit', sectionId, field, value: next }
        : { type: 'storefront-inline-edit', field, value: next },
    );
  };

  const editableClass = [
    className ?? '',
    'inline-editable outline-2 outline-dashed outline-offset-2 outline-burgundy-400 hover:outline-solid focus:outline-solid focus:bg-burgundy-50/40 rounded-sm',
  ].join(' ');

  const toolbarEl = toolbarOpen && ref.current ? (
    <InlineFormatToolbar
      anchorEl={ref.current}
      value={override}
      onChange={applyStyle}
      onReset={resetStyle}
      onClose={() => setToolbarOpen(false)}
      fillValue={
        override.backgroundColor
          ? String(override.backgroundColor)
          : ref.current
            ? getComputedStyle(findFillTarget(ref.current)).backgroundColor
            : undefined
      }
    />
  ) : null;

  // Style-only: not contentEditable; clicking opens the toolbar to restyle
  // (colour/size/font/weight/spacing/Fill). The value/children stay put.
  if (styleOnly) {
    return (
      <>
        <Tag
          ref={ref as any}
          className={[className ?? '', 'inline-editable outline-2 outline-dashed outline-offset-2 outline-burgundy-400 hover:outline-solid rounded-sm', 'cursor-pointer'].join(' ')}
          style={style}
          data-field={field}
          tabIndex={0}
          title="Click to restyle (colour, size, font)"
          onClick={() => sectionId != null && setToolbarOpen(true)}
        >
          {children ?? value}
        </Tag>
        {toolbarEl}
      </>
    );
  }

  return (
    <>
      <Tag
        ref={ref as any}
        className={editableClass}
        style={style}
        contentEditable
        suppressContentEditableWarning
        data-field={field}
        data-placeholder={placeholder ?? ''}
        onFocus={() => sectionId != null && setToolbarOpen(true)}
        onBlur={handleBlur}
      >
        {children ?? value}
      </Tag>
      {toolbarEl}
    </>
  );
}
